/* eslint-disable max-len */
import { Box, Flex, Grid, HStack, Spinner, Stack, Text, VStack } from '@chakra-ui/react';
import TextSection from '@components/articles/SeoTextSection';
import Button, { ButtonVariants } from '@components/interactive/Button';
import DefaultLayout from '@components/layout/layout/DefaultLayout';
import { CTAOptions, OmniBox } from '@components/omniBox';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import { PhoneNumbers } from '@constants/phoneNumbers';
import { Routes } from '@constants/routes';
import { antlerStyle, navHeight } from '@constants/styles';
import { faClone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { getFeaturedPosts, Post } from '@utils/contentful';
import { encodeSlug } from '@utils/slugs';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FaFacebook, FaTwitter } from 'react-icons/fa';
import { titleCase } from 'title-case';
import { FsmColors } from '../../../../chakra-ui/fsmTheme';
import useFsmToast from '../../../../hooks/useCustomToast';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import Body2 from '../../../typography/Body2';
import { RegionalHeading } from '../RegionalHeader';
import PlanF from './PlanF';
import PlanG from './PlanG';
import PlanN from './PlanN';
import CoverageOutline from './common/CoverageOutline';

const coverage = [
  { covered: true, name: 'Part A Coinsurance' },
  { covered: true, name: 'Part A Hospice Care Coinsurance + Copay' },
  { covered: true, name: 'Part A Deductible' },
  { covered: true, name: 'Blood (3 Pints)' },
  { covered: true, name: 'Part B Coinsurance + Copay' },
  { covered: true, name: 'Part B Excess Charge' },
  { covered: false, name: 'Part B Deductible' },
];

const statesOutlawsExcessCharges = [
  'Connecticut',
  'Massachusetts',
  'Minnesota',
  'New York',
  'Ohio',
  'Pennsylvania',
  'Rhode Island',
  'Vermont',
];

type MedSupRegionData = {
  alt: string;
  blurb: string;
  cityList: string[];
  image: string;
  name: string;
  planFAverage: number;
  planFCarriers: string[];
  planGAverage: number;
  planGCarriers: string[];
  planNAverage: number;
  planNCarriers: string[];
  publishDate: string;
  state: string;
  stateCode: string;
  stateSlug: string;
  title: string;
};

const BestMedSupInCity = ({ data }: { data: MedSupRegionData }) => {
  const isMobile = useIsMobile();
  const router = useRouter();
  const { successToast } = useFsmToast();
  const [featuredPosts, setFeaturedPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  const name = titleCase(data.name);
  const offersN = !!data.planNCarriers.length;
  const recommendN = offersN && statesOutlawsExcessCharges.includes(titleCase(data.state));

  // Fetch featured posts from Contentful
  useEffect(() => {
    const fetchFeaturedPosts = async () => {
      try {
        const posts = await getFeaturedPosts();
        setFeaturedPosts(posts);
      } catch (error) {
        console.log('Error fetching featured posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedPosts();
  }, []);

  const renderCta = () => (
    <OmniBox
      cta={CTAOptions.MEDICARE_ADVOCATE}
      phoneNumber={router.asPath.includes('?') ? PhoneNumbers.GOOGLE : PhoneNumbers.MS_SEO}
    />
  );

  // Create SSR-safe share URL
  const shareUrl = `${process.env.BASE_URL}${Routes.bestMedicareSupplement}/${data.stateSlug}/${encodeSlug(name)}`;

  // Sidebar component to avoid duplication
  const SidebarContent = () => (
    <VStack
      spacing={3}
      alignItems="flex-start"
      w="100%"
      bg="white"
      p={[4, 4, 6]}
      borderRadius="md"
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.200"
    >
      {/* Share Section */}
      <Text fontSize="md" fontWeight="bold">
        Share
      </Text>
      {/* Social Share Icons in a compact row */}
      <Flex gap={2} justifyContent="space-between" w="100%">
        <Button
          onClick={() => {
            navigator.clipboard.writeText(shareUrl);
            successToast({ text: 'Link Copied' });
          }}
          size="sm"
          faIcon={faClone}
          variant={ButtonVariants.secondary}
          px={0}
          flex={1}
        />
        <Button
          href={`mailto:?subject=Check this out&body=${encodeURIComponent(shareUrl)}`}
          size="sm"
          faIcon={faEnvelope}
          variant={ButtonVariants.secondary}
          px={0}
          flex={1}
        />
        <Button
          href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          size="sm"
          reactIcon={FaTwitter}
          variant={ButtonVariants.secondary}
          px={0}
          flex={1}
        />
        <Button
          href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
          target="_blank"
          size="sm"
          reactIcon={FaFacebook}
          variant={ButtonVariants.secondary}
          px={0}
          flex={1}
        />
      </Flex>

      {/* Featured Posts Section */}
      <Text fontSize="md" fontWeight="bold" pt={2}>
        Recommended Reading
      </Text>
      {loading ? (
        <HStack justifyContent="center" w="100%">
          <Spinner />
        </HStack>
      ) : (
        <Stack spacing={4} w="100%">
          {featuredPosts.slice(0, isMobile ? 1 : 3).map((post) => (
            <Link href={`${Routes.articles}/${post.slug}`} key={post.slug}>
              <Stack
                spacing={3}
                key={post.slug}
                w="100%"
                p={3}
                bg="gray.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                _hover={{
                  bg: 'white',
                  borderColor: FsmColors.navy,
                }}
                cursor="pointer"
              >
                <Box w="100%">
                  <Image
                    src={post.coverImage.imageUrl}
                    alt={post.coverImage.title}
                    width={260}
                    height={80}
                    style={{
                      borderRadius: 8,
                      objectFit: 'cover',
                      height: '80px',
                      width: '100%',
                    }}
                  />
                </Box>
                <Body2 bold color={FsmColors.navy} noOfLines={2}>
                  {post.title}
                </Body2>
              </Stack>
            </Link>
          ))}
        </Stack>
      )}
    </VStack>
  );

  return (
    <article>
      <DefaultLayout>
        <Stack alignItems="center" spacing={[6, 6, 16]} py={[12, 12, 16, 16]} px={antlerStyle.px}>
          {/* Full-width header at the top */}
          <RegionalHeading
            title={data.title}
            subtitle={`In ${name}, the best choice is plan ${recommendN ? 'G or N' : 'G'}.`}
            imageSrc={data.image}
            imageAlt={data.alt}
            dateString={data.publishDate}
          />

          {/* Mobile sidebar - appears between header and content */}
          <Box
            w="100%"
            maxW={['100%', '100%', '1200px', '1400px', '1600px']}
            display={['block', 'block', 'none', 'none']}
            mt={8}
          >
            <SidebarContent />
          </Box>

          {/* Two-column layout for desktop */}
          <Flex
            w="100%"
            maxW={['100%', '100%', '1200px', '1400px', '1600px']}
            mt={8}
            gap={14}
            align="flex-start"
            direction={['column', 'column', 'row', 'row']}
          >
            {/* Desktop Sidebar - sticky positioned */}
            <Box
              position="sticky"
              top={navHeight + 16}
              w="100%"
              maxW="300px"
              h="fit-content"
              alignSelf="flex-start"
              display={['none', 'none', 'block', 'block']}
            >
              <SidebarContent />
            </Box>

            {/* Main Content - adjusted for wider sidebar */}
            <Box flex="1" maxW={['100%', '100%', 'calc(100% - 320px)', 'calc(100% - 340px)']}>
              <Stack spacing={12} w="100%">
                <TextSection title={`In ${name}, the Best Choice is ${recommendN ? 'G or N' : 'G'}.`}>
                  <Body1>
                    There are bunch of Medicare Supplement plans, each named after a letter of the alphabet. There's
                    Plan A, B (different from Parts A & B), C, D, F, G, K, L, M, N.
                  </Body1>
                  <Body1>
                    <b>
                      {' '}
                      Making sense of this alphabet soup starts by understanding that though there are a lot of plans,
                      only one, sometimes two, make sense to consider: Plan G, and if you turn 65 before 1/1/2020, Plan
                      F.
                    </b>
                  </Body1>
                  <Body1>
                    The reason this is the case gets into the details of how insurance plans work. Suffice it to say
                    that each letter has its own risk pool and all the letters except of G and F have very small pool
                    and net out to offering less coverage than G or F for higher premium.
                  </Body1>
                  <Body1>
                    That's right, Medicare Supplements apart from G and F cost more and offer less coverage.
                  </Body1>
                  <Body1>
                    And if you're turning 65 now, you can't enroll in Plan F. You're just looking at Plan G. Plan F is
                    only available to folks who turned 65 before 1/1/2020.
                  </Body1>
                </TextSection>
                <TextSection title={`Plan G or Medicare Advantage in ${name}?`}>
                  <Body1>
                    Plan G is the Cadillac plan. Comprehensive coverage but not the lowest cost option. Folks who want
                    to spend extra money for the security of comprehensive coverage choose plan G.
                  </Body1>
                  <Body1>
                    Compared to a Medicare Advantage plan, you'll be able to see far more doctors, more of your medical
                    expenses will be covered, and your premium will be higher.
                  </Body1>
                </TextSection>
                <TextSection title={`What does Plan G Cover in ${name}?`}>
                  <Body1>
                    Plan G is possibly the best health insurance coverage available to anyone in the United States. It
                    is a secondary payer to your Part A & B Medicare coverage and covers the following:
                  </Body1>
                  <CoverageOutline coverage={coverage} />

                  <Body1>
                    In short, if you go to a doctor that accepts Medicare (90% of doctors in the U.S.) and have a
                    medically approved procedure (most procedures, but not cosmetic), it will cover everything except
                    your Part B deductible, which is $257. It also covers 80% of medical necessary emergency care you
                    receive outside of the United States.
                  </Body1>
                </TextSection>

                <TextSection title={`When to enroll in Plan G in ${name}?`}>
                  <Body1>
                    Plan G, and all Medicare Supplements, screen members for medical conditions unless they enroll when
                    they turn 65 or come off of their employers health plan.
                  </Body1>
                  <Body1>
                    Put another way, if you enroll in a Medicare supplment when you are eligible to enroll in Medicare
                    Parts A & B (usually turning 65 or retiring), you won't be screened for pre-existing conditions. If
                    choose to wait and enroll later, you will be.
                  </Body1>
                </TextSection>
                <TextSection title={`How to choose your Plan G Plan in in ${name}?`}>
                  <Body1>
                    The benefits of Plan G are defined by the government. This means that Plan G from United Healthcare
                    is the same as Plan G from Mutual of Omaha which is the same as Plan G from Aetna. You get it.
                  </Body1>
                  <Body1>
                    No matter the carrier, the benefits of Plan G are the same because they are defined by the
                    government.
                  </Body1>
                  <Body1>
                    So when you choose Plan G, there are two criteria I'd suggest looking at in addition the price:
                  </Body1>
                  <Body1>
                    <b>Types of Rates</b>
                  </Body1>
                  <Body1>
                    • <b>Attained Age</b> - Most Plan G's get more expensive as you age. This is called "attained age"
                    rating. These plans will typically offer the lowest price for you today. It's impossible to predict
                    how the price will increase, but I suggest guessing a 3-5% increase per year.
                  </Body1>
                  <Body1>
                    • <b>Issue Age</b> - Another type of price is "issue age". This plan sets the price at your age when
                    you enroll. The price doesn't go up as you age. More expensive for today but likely lower cost in
                    the future.
                  </Body1>
                  <Body1>
                    • <b>Community Rated</b> - These are plans where premiums don't increase with age. Rare.
                  </Body1>
                  <Body1>
                    <b>Customer Service</b>
                  </Body1>
                  <Body1>
                    When you call your plan because something goes wrong, are you going to talk to an American or
                    someone in the Philippines. Personally, I think this matters. Mutual of Omaha is fantastic in this
                    regard.
                  </Body1>
                </TextSection>
                {!!data.planGCarriers.length && (
                  <PlanG name={name} carriers={data.planGCarriers} renderCta={renderCta} />
                )}
                {recommendN && (
                  <PlanN
                    name={name}
                    carriers={data.planNCarriers}
                    averagePremium={data.planNAverage}
                    renderCta={renderCta}
                  />
                )}

                {/* Always render Plan F information, but conditionally render sub info (i.e. average price / cheapest carriers) */}
                <PlanF
                  name={name}
                  carriers={data.planFCarriers}
                  averagePremium={data.planFAverage}
                  stateCode={data.stateCode}
                  renderCta={renderCta}
                />
                {data.cityList && data.cityList.length > 0 && (
                  <Stack py="40px" spacing={6} alignItems="center">
                    <Header as="h3">Other Cities in {data.state}</Header>
                    <Grid
                      templateColumns={[
                        'repeat(1, 1fr)',
                        'repeat(2, 1fr)',
                        'repeat(3, 1fr)',
                        'repeat(4, 1fr)',
                        'repeat(5, 1fr)',
                      ]}
                      gap={4}
                      w="100%"
                      maxW={['100%', '100%', '800px', '1000px', '1200px']}
                    >
                      {data.cityList.map((city) => (
                        <Button
                          key={encodeSlug(city)}
                          href={`${Routes.bestMedicareSupplement}/${encodeSlug(data.state)}/${encodeSlug(city)}`}
                          variant={ButtonVariants.secondary}
                          size="sm"
                          px={4}
                        >
                          {titleCase(city)}
                        </Button>
                      ))}
                    </Grid>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Flex>
        </Stack>
      </DefaultLayout>
    </article>
  );
};

export default BestMedSupInCity;
